// components/AnnotationCanvas.tsx
import React, { useCallback, forwardRef, useImperativeHandle, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { hexToRgb } from '#lib/canvas-utils';
import { isInertSurface } from '#lib/keyboard-shortcuts';
import { getWorkspaceRasterDimensions } from './workspaceRasterUtils';
import { useKernel } from '@/kernel/Kernel';
import { getCanvasMouseCoordinates } from './canvasPointerUtils';
import {
  useKernelStore,
  useKernelStoreApi,
} from '@/kernel/kernelStores';
import { ArrowUp, Check, X, MoreHorizontal } from '@/components/ui/lucide-memo';
import { MaskShape, AnnotationComment } from '#types/types';
import { resolveWorkspaceAnnotationOpacity } from '#lib/workspace-annotations';
import FloatingCanvasInput from './FloatingCanvasInput';
import { distanceToSegment, pointInPolygon, type MaskPoint } from './inpaintMaskGeometry';

const ANNOTATION_ERASER_MULTIPLIER = 6;

// Comments have their own fixed colour (Figma-style blue pin), independent of the
// mask/annotation drawing colour.
const COMMENT_COLOR = '#3B82F6';
const POLYGON_CLICK_MOVE_TOLERANCE_PX = 3;
// Mask Pen renders a crisp Figma-style blue vector overlay: translucent fill, solid outline,
// and white anchor handles. Shapes are stored as vectors and rasterized to B/W only at
// generation time, so these display colours never leak into the mask sent to Ideogram.
const MASK_OUTLINE = '#0D99FF';
const MASK_FILL = 'rgba(13, 153, 255, 0.18)';
const MASK_STROKE_FILL = 'rgba(13, 153, 255, 0.35)';
const MASK_ANCHOR_FILL = '#ffffff';
const MASK_GUIDE = 'rgba(255, 45, 45, 0.9)'; // thin red Figma-style alignment guide

// Deep-clone shapes so anchor edits never mutate the shared store array in place.
const cloneMaskShapes = (shapes: MaskShape[]): MaskShape[] =>
  shapes.map((s) => ({ ...s, points: s.points.map((p) => ({ ...p })) }));

// Four corners (clockwise from top-left) of the axis-aligned rectangle spanning a and b.
// The rect sub-mode commits these as a regular vector polygon.
const rectCorners = (a: MaskPoint, b: MaskPoint): MaskPoint[] => {
  const minX = Math.min(a.x, b.x), maxX = Math.max(a.x, b.x);
  const minY = Math.min(a.y, b.y), maxY = Math.max(a.y, b.y);
  return [{ x: minX, y: minY }, { x: maxX, y: minY }, { x: maxX, y: maxY }, { x: minX, y: maxY }];
};

// Distance (frame-raster px) within which the cursor snaps to the first anchor to close a polygon.
// Shared by the draft renderer (visual snap) and the click handler (commit) so they stay in sync.
const maskCloseTolerance = (scale: number): number => Math.max(10, 12 * scale);

const clampShapeTranslation = (
  points: MaskPoint[],
  dx: number,
  dy: number,
  width: number,
  height: number,
): { dx: number; dy: number } => {
  if (points.length === 0) return { dx: 0, dy: 0 };
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const point of points) {
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  }
  return {
    dx: Math.min(width - maxX, Math.max(-minX, dx)),
    dy: Math.min(height - maxY, Math.max(-minY, dy)),
  };
};

// Figma-style smart alignment: if the active point is within `threshold` (frame-raster px) of
// another node's x, snap its x and report a vertical guide; likewise for y / a horizontal guide.
type MaskAlignment = { x: number; y: number; vX: number | null; hY: number | null };
const computeAlignmentSnap = (active: MaskPoint, others: MaskPoint[], threshold: number): MaskAlignment => {
  let vX: number | null = null;
  let hY: number | null = null;
  let bestVDist = threshold;
  let bestHDist = threshold;
  for (const o of others) {
    const dx = Math.abs(active.x - o.x);
    if (dx <= bestVDist) { bestVDist = dx; vX = o.x; }
    const dy = Math.abs(active.y - o.y);
    if (dy <= bestHDist) { bestHDist = dy; hY = o.y; }
  }
  return { x: vX ?? active.x, y: hY ?? active.y, vX, hY };
};

// Transient overlay rendered on top of the committed mask while drawing/dragging.
type MaskOverlay = {
  draft: MaskPoint[];
  rubber: MaskPoint | null;
  brush: MaskPoint[] | null;
  brushCursor: MaskPoint | null;
  guideVX: number | null;
  guideHY: number | null;
  snap: MaskPoint | null; // small red "X" marker at the alignment / close-snap target
};
const EMPTY_MASK_OVERLAY: MaskOverlay = { draft: [], rubber: null, brush: null, brushCursor: null, guideVX: null, guideHY: null, snap: null };

function getTimeAgo(timestamp: number) {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const zoomLevelCssVar = 'var(--zoom-level, 1)';
const inverseZoomLevelCssVar = 'var(--inverse-zoom-level, 1)';

const stopMouseEventPropagation = (event: React.MouseEvent) => event.stopPropagation();

const PIN_FRAME_STYLE: React.CSSProperties = {
  width: 32,
  height: 32,
  backgroundColor: '#2e2e2e',
  borderRadius: '50% 50% 50% 2px',
};
const PIN_AVATAR_STYLE: React.CSSProperties = { width: 26, height: 26 };
const HOVER_CARD_AVATAR_STYLE: React.CSSProperties = { width: 28, height: 28 };
const ANNOTATION_CANVAS_STYLE: React.CSSProperties = { width: '100%', height: '100%', imageRendering: 'pixelated', opacity: 0.8 };
const PREVIEW_CANVAS_STYLE: React.CSSProperties = { width: '100%', height: '100%' };
const MASK_CANVAS_STYLE: React.CSSProperties = { width: '100%', height: '100%', opacity: 0 };
const MASK_SVG_STYLE: React.CSSProperties = { width: '100%', height: '100%', overflow: 'visible' };

type AnnotationCommentReply = NonNullable<AnnotationComment['replies']>[number];

interface CommentPinProps {
  commentId: string;
  index: number;
  x: number;
  y: number;
  displayScale: number;
  text: string;
  color?: string;
  author?: string;
  authorAvatarUrl?: string;
  timestamp?: number;
  onSelect: (commentId: string) => void;
}

const CommentPin = React.memo<CommentPinProps>(({
  commentId,
  index,
  x,
  y,
  displayScale,
  text,
  color,
  author,
  authorAvatarUrl,
  timestamp,
  onSelect,
}) => {
  const pinStyle = useMemo<React.CSSProperties>(() => ({
    left: x * displayScale,
    top: y * displayScale,
    transform: `translate(0, -100%) scale(${inverseZoomLevelCssVar})`,
    transformOrigin: 'bottom left',
    zIndex: 10,
  }), [x, y, displayScale]);
  const fallbackAvatarStyle = useMemo<React.CSSProperties>(() => ({
    width: 26,
    height: 26,
    backgroundColor: color ?? COMMENT_COLOR,
  }), [color]);
  const handleClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    onSelect(commentId);
  }, [commentId, onSelect]);

  return (
    <div
      className="group absolute cursor-pointer pointer-events-auto"
      style={pinStyle}
      onMouseDown={stopMouseEventPropagation}
      onClick={handleClick}
    >
      {/* Teardrop pin framing the author avatar */}
      <div className="flex items-center justify-center shadow-lg" style={PIN_FRAME_STYLE}>
        {authorAvatarUrl ? (
          <img
            src={authorAvatarUrl}
            alt={author || 'User'}
            className="rounded-full object-cover"
            style={PIN_AVATAR_STYLE}
          />
        ) : (
          <div
            className="flex items-center justify-center rounded-full text-[11px] font-semibold text-white"
            style={fallbackAvatarStyle}
          >
            {index + 1}
          </div>
        )}
      </div>
      {/* Hover card */}
      <div className="invisible absolute left-full top-0 ml-2 w-max max-w-[260px] rounded-xl bg-[#2e2e2e] p-3 opacity-0 shadow-2xl transition-opacity duration-100 pointer-events-none group-hover:visible group-hover:opacity-100">
        <div className="flex items-center gap-2 whitespace-nowrap">
          {authorAvatarUrl && (
            <img
              src={authorAvatarUrl}
              alt=""
              className="rounded-full object-cover"
              style={HOVER_CARD_AVATAR_STYLE}
            />
          )}
          <div className="text-sm">
            <span className="font-semibold text-white">{author || 'User'}</span>
            {timestamp && (
              <span className="ml-1.5 text-white/40">{getTimeAgo(timestamp)}</span>
            )}
          </div>
        </div>
        <p className="mt-1.5 whitespace-pre-wrap break-words text-sm text-white/90">{text}</p>
      </div>
    </div>
  );
});
CommentPin.displayName = 'CommentPin';

interface ThreadRowProps {
  rowId: string;
  isRoot: boolean;
  text: string;
  author?: string;
  authorAvatarUrl?: string;
  timestamp?: number;
  onDelete: (rowId: string, isRoot: boolean) => void;
}

const ThreadRow = React.memo<ThreadRowProps>(({ rowId, isRoot, text, author, authorAvatarUrl, timestamp, onDelete }) => {
  const handleDelete = useCallback(() => onDelete(rowId, isRoot), [onDelete, rowId, isRoot]);
  return (
    <div className="group/row flex gap-2.5">
      <div className="h-7 w-7 shrink-0 overflow-hidden rounded-full bg-[#3a3a3a]">
        {authorAvatarUrl && <img src={authorAvatarUrl} alt="" className="h-full w-full object-cover" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-semibold text-white">{author || 'User'}</span>
          {timestamp && <span className="text-xs text-white/40">{getTimeAgo(timestamp)}</span>}
          <button
            type="button"
            title={isRoot ? 'Delete thread' : 'Delete reply'}
            onClick={handleDelete}
            className="ml-auto flex h-6 w-6 items-center justify-center rounded-full text-white/40 opacity-0 transition hover:bg-white/10 hover:text-white group-hover/row:opacity-100"
          >
            <MoreHorizontal size={15} />
          </button>
        </div>
        <p className="mt-0.5 whitespace-pre-wrap break-words text-sm text-white/90">{text}</p>
      </div>
    </div>
  );
});
ThreadRow.displayName = 'ThreadRow';

interface CommentThreadPanelProps {
  threadId: string;
  x: number;
  y: number;
  displayScale: number;
  text: string;
  author?: string;
  authorAvatarUrl?: string;
  timestamp?: number;
  replies?: AnnotationCommentReply[];
  replyText: string;
  currentUserAvatarUrl?: string;
  onResolve: () => void;
  onClose: () => void;
  onReplyTextChange: (value: string) => void;
  onReplySubmit: () => void;
  onDeleteRow: (rowId: string, isRoot: boolean) => void;
}

const CommentThreadPanel = React.memo<CommentThreadPanelProps>(({
  threadId,
  x,
  y,
  displayScale,
  text,
  author,
  authorAvatarUrl,
  timestamp,
  replies,
  replyText,
  currentUserAvatarUrl,
  onResolve,
  onClose,
  onReplyTextChange,
  onReplySubmit,
  onDeleteRow,
}) => {
  const panelStyle = useMemo<React.CSSProperties>(() => ({
    left: `calc(${x * displayScale}px + (10px / ${zoomLevelCssVar}))`,
    top: `calc(${y * displayScale}px + (10px / ${zoomLevelCssVar}))`,
    transform: `scale(${inverseZoomLevelCssVar})`,
    transformOrigin: 'top left',
  }), [x, y, displayScale]);
  const rows = useMemo(() => [
    { id: threadId, author, authorAvatarUrl, text, timestamp, isRoot: true },
    ...(replies ?? []).map((r) => ({ id: r.id, author: r.author, authorAvatarUrl: r.authorAvatarUrl, text: r.text, timestamp: r.timestamp, isRoot: false })),
  ], [threadId, author, authorAvatarUrl, text, timestamp, replies]);
  const handleReplyChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onReplyTextChange(event.target.value);
  }, [onReplyTextChange]);
  const handleReplyKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); onReplySubmit(); }
    if (event.key === 'Escape') { event.preventDefault(); onClose(); }
  }, [onReplySubmit, onClose]);

  return (
    <div
      className="absolute z-50 pointer-events-auto"
      style={panelStyle}
      onMouseDown={stopMouseEventPropagation}
    >
      <div className="w-[340px] overflow-hidden rounded-2xl border border-white/10 bg-[#353535] shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-2.5">
          <span className="text-sm font-semibold text-white">Comment</span>
          <div className="flex items-center gap-0.5 text-white/60">
            <button type="button" title="Resolve" onClick={onResolve} className="flex h-7 w-7 items-center justify-center rounded-full transition-colors hover:bg-white/10 hover:text-white">
              <Check size={16} />
            </button>
            <button type="button" title="Close" onClick={onClose} className="flex h-7 w-7 items-center justify-center rounded-full transition-colors hover:bg-white/10 hover:text-white">
              <X size={16} />
            </button>
          </div>
        </div>
        {/* Comment + replies */}
        <div className="hide-scrollbar max-h-[280px] space-y-4 overflow-y-auto px-4 py-3">
          {rows.map((row) => (
            <ThreadRow
              key={row.id}
              rowId={row.id}
              isRoot={row.isRoot}
              text={row.text}
              author={row.author}
              authorAvatarUrl={row.authorAvatarUrl}
              timestamp={row.timestamp}
              onDelete={onDeleteRow}
            />
          ))}
        </div>
        {/* Reply composer */}
        <div className="flex items-center gap-2 px-3 pb-3 pt-1">
          <div className="h-7 w-7 shrink-0 overflow-hidden rounded-full bg-[#3a3a3a]">
            {currentUserAvatarUrl && <img src={currentUserAvatarUrl} alt="" className="h-full w-full object-cover" />}
          </div>
          <div className="flex flex-1 items-center gap-1 rounded-full border border-white/10 bg-[#3a3a3a] py-1.5 pl-4 pr-1.5">
            <input
              value={replyText}
              placeholder="Reply"
              onChange={handleReplyChange}
              onKeyDown={handleReplyKeyDown}
              className="flex-1 appearance-none border-0 bg-transparent text-sm text-white placeholder-white/40 outline-none focus:outline-none focus:ring-0"
            />
            <button
              type="button"
              onClick={onReplySubmit}
              disabled={!replyText.trim()}
              title="Reply"
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25 disabled:opacity-40 disabled:hover:bg-white/15"
            >
              <ArrowUp size={15} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});
CommentThreadPanel.displayName = 'CommentThreadPanel';

export interface AnnotationCanvasHandle {
  handleMouseDown: (event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => Promise<boolean | undefined>;
  handleMouseMove: (event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => boolean | undefined;
  handleMouseUp: (event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => boolean | undefined;
  hasActiveMaskDraft: () => boolean;
}

interface AnnotationCanvasProps {
  projectId: string;
  workspaceId: string;
  isActiveWorkspace: boolean;
}

const AnnotationCanvas = React.memo(forwardRef<AnnotationCanvasHandle, AnnotationCanvasProps>(({ projectId, workspaceId, isActiveWorkspace }, ref) => {
  const { kernel } = useKernel();
  // Stable store handle: the drawing callbacks read tool state at execution time instead of
  // capturing it through their dependency arrays.
  const kernelStore = useKernelStoreApi();
  const dbUserId = useKernelStore((state) => state.userContext.dbUserId);
  const currentUserAvatarUrl = dbUserId ? `/api/user/avatar?userId=${dbUserId}` : undefined;
  const activeTool = useKernelStore((state) => state.activeTool);
  const annotationBrushSize = useKernelStore((state) => state.annotationBrushSize);
  const annotationColor = useKernelStore((state) => state.annotationColor);
  const inpaintShapes = useKernelStore((state) => state.inpaintMaskShapes[workspaceId] ?? null);
  const masksHidden = useKernelStore((state) => state.masksHidden);
  const isShiftKeyPressed = useKernelStore((state) => state.isShiftKeyPressed === true);
  const activeWorkspace = useKernelStore((state) => state.inventory.StudioWorkspace.registry[workspaceId] ?? null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const maskCanvasRef = useRef<HTMLCanvasElement>(null); // transparent; only the pointer-coordinate reference
  const maskOverlayRef = useRef<MaskOverlay>(EMPTY_MASK_OVERLAY);
  const [, setMaskTick] = useState(0);
  const maskDraftPointsRef = useRef<MaskPoint[]>([]);
  const brushDraftRef = useRef<MaskPoint[]>([]);
  const rectStartRef = useRef<MaskPoint | null>(null);
  const editDragRef = useRef<{ shapeIndex: number; pointIndex: number } | null>(null);
  const workingShapesRef = useRef<MaskShape[] | null>(null);
  const moveShapeRef = useRef<{ shapeIndex: number } | null>(null);
  const moveLastRef = useRef<MaskPoint | null>(null);
  const lastMousePos = useRef<{ x: number; y: number } | null>(null);
  const strokeStartPos = useRef<{ x: number; y: number } | null>(null);
  const strokeMoved = useRef(false);
  const lastPlacedPoint = useRef<{ x: number; y: number } | null>(null);
  const lastHoverPos = useRef<{ x: number; y: number } | null>(null);
  const isDrawing = useKernelStore((state) => state.isDrawing);
  const frameRect = activeWorkspace?.frameRect ?? { x: 0, y: 0, w: 0, h: 0 };
  // Callbacks read the frame rect fresh instead of depending on `frameRect`: that derived value is
  // a brand new object on every render whenever the workspace carries none.
  const getFrameRect = useCallback(() => (
    kernel.current?.getWorkspace(workspaceId)?.frameRect ?? { x: 0, y: 0, w: 0, h: 0 }
  ), [kernel, workspaceId]);
  const activeVersionId =
    (typeof activeWorkspace?.activeVersionId === 'string' && activeWorkspace.activeVersionId.trim().length > 0)
      ? activeWorkspace.activeVersionId
      : ((typeof activeWorkspace?.validatedVersionId === 'string' && activeWorkspace.validatedVersionId.trim().length > 0)
        ? activeWorkspace.validatedVersionId
        : null);
  const activeWorkspaceVersion = activeVersionId
    ? (activeWorkspace?.versions ?? []).find((version) => version.id === activeVersionId) ?? null
    : null;
  const activeWorkspaceAnnotationMask =
    activeVersionId && activeWorkspace?.annotationMaskVersionId === activeVersionId
      ? (activeWorkspace.annotationMask ?? null)
      : (activeWorkspaceVersion?.annotationMask ?? null);
  // Stable identity so the callbacks reading it (and the imperative handle they feed) do not churn
  // on every pointer-driven re-render.
  const annotationComments = useMemo(
    () => activeWorkspaceVersion?.annotationComments ?? [],
    [activeWorkspaceVersion],
  );
  const annotationCanvasStyle = useMemo<React.CSSProperties>(() => ({
    ...ANNOTATION_CANVAS_STYLE,
    opacity: resolveWorkspaceAnnotationOpacity(activeWorkspace?.annotationOpaque),
  }), [activeWorkspace?.annotationOpaque]);

  const [pendingComment, setPendingComment] = useState<{ id: string; x: number; y: number; mode: 'new' | 'edit'; color: string; author?: string; timestamp?: number } | null>(null);
  const [pendingCommentText, setPendingCommentText] = useState('');
  const maskSvgIdBase = useMemo(() => `inpaint-mask-${workspaceId.replace(/[^a-zA-Z0-9_-]/g, '_')}`, [workspaceId]);
  // Id of the comment whose thread panel (comment + replies + reply composer) is open.
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');

  const persistWorkspaceAnnotationMask = useCallback(async (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement) => {
    await kernel.current?.commitWorkspaceAnnotationMask(
      workspaceId,
      ctx.getImageData(0, 0, canvas.width, canvas.height),
    );
  }, [kernel, workspaceId]);

  const stampBrush = useCallback((ctx: CanvasRenderingContext2D, point: { x: number; y: number }, mode: 'draw' | 'erase') => {
    const { annotationBrushSize, annotationBrushHardness, annotationColor } = kernelStore.getState();
    const radius = Math.max(0.5, annotationBrushSize / 2);
    if (mode === 'erase') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.arc(point.x, point.y, radius * ANNOTATION_ERASER_MULTIPLIER, 0, 2 * Math.PI);
      ctx.fill();
      ctx.globalCompositeOperation = 'source-over';
      return;
    }
    const hardened = Math.min(Math.max(annotationBrushHardness, 0), 1);
    if (hardened >= 0.98) {
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = annotationColor;
      ctx.beginPath();
      ctx.arc(point.x, point.y, radius, 0, 2 * Math.PI);
      ctx.fill();
      return;
    }
    const innerRadius = radius * hardened;
    const gradient = ctx.createRadialGradient(point.x, point.y, innerRadius, point.x, point.y, radius);
    const [r, g, b] = hexToRgb(annotationColor) || [255, 255, 255];
    gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 1)`);
    gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(point.x, point.y, radius, 0, 2 * Math.PI);
    ctx.fill();
  }, [kernelStore]);

  const drawBrushedSegment = useCallback((ctx: CanvasRenderingContext2D, from: { x: number; y: number }, to: { x: number; y: number }) => {
    const { annotationBrushSize } = kernelStore.getState();
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const distance = Math.hypot(dx, dy);
    const step = Math.max(1, annotationBrushSize / 3);
    const steps = Math.max(1, Math.ceil(distance / step));
    for (let i = 0; i <= steps; i++) {
      const t = steps === 0 ? 0 : i / steps;
      const point = { x: from.x + dx * t, y: from.y + dy * t };
      stampBrush(ctx, point, 'draw');
    }
  }, [kernelStore, stampBrush]);

  const applyEraserStroke = useCallback((ctx: CanvasRenderingContext2D, from: { x: number; y: number }, to: { x: number; y: number }) => {
    const { annotationBrushSize } = kernelStore.getState();
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = Math.max(1, annotationBrushSize * ANNOTATION_ERASER_MULTIPLIER);
    ctx.stroke();
    ctx.restore();
  }, [kernelStore]);

  // ----- Mask Pen tool (Figma-style blue editable vector mask) -----
  // Shapes live in the kernel store (`inpaintMaskShapes`) in frame-raster space and are
  // rasterized to B/W only at generation time. Committed shapes (fill + outline + draggable
  // anchors) render on the mask canvas; the in-progress draft renders on the preview canvas.
  const getStoreShapes = useCallback((): MaskShape[] => {
    return kernel.current?.getInpaintMaskShapes()[workspaceId] ?? [];
  }, [kernel, workspaceId]);

  // The mask renders as an SVG overlay (crisp vectors at any zoom). These helpers stash transient
  // draft/guide state in a ref and bump a tick so the SVG re-renders.
  const forceMaskRender = useCallback(() => setMaskTick((t) => (t + 1) % 1_000_000), []);

  const applyMaskShapes = useCallback((shapes: MaskShape[]) => {
    kernel.current?.setInpaintMaskShapes((prev) => ({ ...prev, [workspaceId]: cloneMaskShapes(shapes) }));
  }, [kernel, workspaceId]);

  const persistShapes = useCallback((shapes: MaskShape[], description = 'Update inpaint mask') => {
    const previousShapes = cloneMaskShapes(getStoreShapes());
    const nextShapes = cloneMaskShapes(shapes);
    kernel.current?.executeAction({
      description,
      undo: () => {
        applyMaskShapes(previousShapes);
        forceMaskRender();
      },
      redo: () => {
        applyMaskShapes(nextShapes);
        forceMaskRender();
      },
    });
  }, [applyMaskShapes, forceMaskRender, getStoreShapes, kernel]);

  const clearInpaintMask = useCallback(() => {
    persistShapes([], 'Clear inpaint mask');
  }, [persistShapes]);

  // Find a committed polygon anchor under the cursor (topmost first).
  const findAnchorAt = useCallback((coords: MaskPoint, hitRadius: number) => {
    const shapes = workingShapesRef.current ?? getStoreShapes();
    for (let si = shapes.length - 1; si >= 0; si--) {
      const s = shapes[si];
      if (!s || s.kind !== 'polygon') continue;
      for (let pi = 0; pi < s.points.length; pi++) {
        const p = s.points[pi];
        if (p && Math.hypot(coords.x - p.x, coords.y - p.y) <= hitRadius) {
          return { shapeIndex: si, pointIndex: pi };
        }
      }
    }
    return null;
  }, [getStoreShapes]);

  // Find a committed polygon edge under the cursor, returning where to insert a new anchor.
  const findEdgeAt = useCallback((coords: MaskPoint, hitRadius: number) => {
    const shapes = workingShapesRef.current ?? getStoreShapes();
    for (let si = shapes.length - 1; si >= 0; si--) {
      const s = shapes[si];
      if (!s || s.kind !== 'polygon' || s.points.length < 2) continue;
      for (let pi = 0; pi < s.points.length; pi++) {
        const a = s.points[pi];
        const b = s.points[(pi + 1) % s.points.length];
        if (a && b && distanceToSegment(coords, a, b) <= hitRadius) {
          return { shapeIndex: si, insertIndex: pi + 1 };
        }
      }
    }
    return null;
  }, [getStoreShapes]);

  // Topmost committed polygon whose filled area contains the cursor (select-tool "move whole mask").
  const findShapeContaining = useCallback((coords: MaskPoint): number => {
    const shapes = workingShapesRef.current ?? getStoreShapes();
    for (let si = shapes.length - 1; si >= 0; si--) {
      const s = shapes[si];
      if (!s || s.kind !== 'polygon' || s.points.length < 3) continue;
      if (pointInPolygon(coords, s.points)) return si;
    }
    return -1;
  }, [getStoreShapes]);

  // All committed polygon anchors (optionally excluding one), used as alignment targets.
  const collectAnchorPoints = useCallback((exclude?: Set<string>): MaskPoint[] => {
    const shapes = workingShapesRef.current ?? getStoreShapes();
    const out: MaskPoint[] = [];
    for (let si = 0; si < shapes.length; si++) {
      const s = shapes[si];
      if (!s || s.kind !== 'polygon') continue;
      for (let pi = 0; pi < s.points.length; pi++) {
        if (exclude?.has(`${si}:${pi}`)) continue;
        const p = s.points[pi];
        if (p) out.push({ x: p.x, y: p.y });
      }
    }
    return out;
  }, [getStoreShapes]);

  const renderAlignmentGuides = useCallback((vX: number | null, hY: number | null, point?: MaskPoint) => {
    maskOverlayRef.current = {
      ...EMPTY_MASK_OVERLAY,
      guideVX: vX,
      guideHY: hY,
      snap: (vX !== null || hY !== null) ? (point ?? null) : null,
    };
    forceMaskRender();
  }, [forceMaskRender]);

  const commitMaskPolygon = useCallback((points: MaskPoint[]) => {
    if (points.length < 3) return;
    persistShapes([...getStoreShapes(), { kind: 'polygon', points: points.map((p) => ({ ...p })) }], 'Draw inpaint mask polygon');
  }, [getStoreShapes, persistShapes]);

  // The committed mask renders declaratively in the SVG (from the store, or the live working copy
  // during a drag); this just triggers a re-render.
  const renderCommittedMask = useCallback(() => {
    forceMaskRender();
  }, [forceMaskRender]);

  // Compute the transient draft / brush / guide overlay and stash it for the SVG to render.
  const renderMaskDraft = useCallback((cursor: MaskPoint | null) => {
    const { activeTool, maskPenMode } = kernelStore.getState();
    if (!isActiveWorkspace || activeTool !== 'maskPen') {
      maskOverlayRef.current = EMPTY_MASK_OVERLAY;
      forceMaskRender();
      return;
    }
    const { scale } = getWorkspaceRasterDimensions(getFrameRect());

    if (maskPenMode === 'brush') {
      maskOverlayRef.current = { ...EMPTY_MASK_OVERLAY, brush: [...brushDraftRef.current], brushCursor: cursor };
      forceMaskRender();
      return;
    }

    if (maskPenMode === 'rect') {
      const start = rectStartRef.current;
      if (!start || !cursor) { maskOverlayRef.current = EMPTY_MASK_OVERLAY; forceMaskRender(); return; }
      // draft = 4 corners, rubber = first corner: the overlay polyline closes the rectangle outline.
      const corners = rectCorners(start, cursor);
      maskOverlayRef.current = { draft: corners, rubber: corners[0] ?? null, brush: null, brushCursor: null, guideVX: null, guideHY: null, snap: null };
      forceMaskRender();
      return;
    }

    const pts = maskDraftPointsRef.current;
    const start = pts[0];
    if (!start) {
      maskOverlayRef.current = EMPTY_MASK_OVERLAY;
      forceMaskRender();
      return;
    }
    const closeTolerance = maskCloseTolerance(scale);
    const canClose = pts.length >= 3 && !!cursor && Math.hypot(cursor.x - start.x, cursor.y - start.y) <= closeTolerance;
    let rubber = cursor;
    let guideVX: number | null = null;
    let guideHY: number | null = null;
    let snap: MaskPoint | null = null;
    if (canClose) {
      rubber = start;
      snap = start; // red X marks the close target
    } else if (cursor) {
      // Exclude the last placed point (the segment's own start) so the new segment never locks to
      // orthogonal; alignment to other nodes still snaps.
      const a = computeAlignmentSnap(cursor, [...collectAnchorPoints(), ...pts.slice(0, -1)], Math.max(6, 7 * scale));
      rubber = { x: a.x, y: a.y };
      guideVX = a.vX;
      guideHY = a.hY;
      if (a.vX !== null || a.hY !== null) snap = rubber; // red X at the aligned point
    }
    maskOverlayRef.current = { draft: pts.map((p) => ({ ...p })), rubber, brush: null, brushCursor: null, guideVX, guideHY, snap };
    forceMaskRender();
  }, [kernelStore, isActiveWorkspace, collectAnchorPoints, forceMaskRender, getFrameRect]);

  const handleMaskPenMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>): boolean => {
    const maskCanvas = maskCanvasRef.current;
    if (!maskCanvas) return false;
    const coords = getCanvasMouseCoordinates(event, maskCanvas, { clamp: true });
    const { activeTool, maskPenMode } = kernelStore.getState();
    const { scale } = getWorkspaceRasterDimensions(getFrameRect());
    const hitRadius = Math.max(8, 9 * scale);
    const isSelect = activeTool === 'canvasControl' || activeTool === null;
    const isAlt = !('touches' in event) && (event as React.MouseEvent).altKey;

    // Freehand brush is a Mask Pen sub-mode only.
    if (!isSelect && maskPenMode === 'brush') {
      kernel.current?.setIsDrawing(true);
      brushDraftRef.current = [coords];
      renderMaskDraft(coords);
      return true;
    }

    // Rectangle is a Mask Pen sub-mode only: drag from the press point to define the rect.
    if (!isSelect && maskPenMode === 'rect') {
      rectStartRef.current = coords;
      kernel.current?.setIsDrawing(true);
      renderMaskDraft(coords);
      return true;
    }

    // While a polygon draft is in progress (pen only): close near the first anchor, else append.
    const draft = maskDraftPointsRef.current;
    if (!isSelect && draft.length > 0) {
      const first = draft[0];
      if (draft.length >= 3 && first) {
        const closeTolerance = maskCloseTolerance(scale);
        if (Math.hypot(coords.x - first.x, coords.y - first.y) <= closeTolerance) {
          commitMaskPolygon(draft);
          maskDraftPointsRef.current = [];
          renderMaskDraft(null);
          return true;
        }
      }
      const placeSnap = computeAlignmentSnap(coords, [...collectAnchorPoints(), ...draft.slice(0, -1)], Math.max(6, 7 * scale));
      const placed = { x: placeSnap.x, y: placeSnap.y };
      maskDraftPointsRef.current = [...draft, placed];
      renderMaskDraft(placed);
      return true;
    }

    // Editing committed shapes. Node editing (move/remove a node, move the whole shape) is the
    // Select tool's job; the Mask Pen only adds nodes.
    if (isSelect) {
      // Select tool: drag a node to reposition it (Alt-click removes it); else move the whole
      // shape. A miss returns false so the click falls through to the normal select behaviour.
      const anchor = findAnchorAt(coords, hitRadius);
      if (anchor) {
        if (isAlt) {
          const shapes = cloneMaskShapes(getStoreShapes());
          const s = shapes[anchor.shapeIndex];
          if (s) {
            if (s.points.length <= 3) shapes.splice(anchor.shapeIndex, 1);
            else s.points.splice(anchor.pointIndex, 1);
            persistShapes(shapes, 'Delete inpaint mask point');
          }
          return true;
        }
        workingShapesRef.current = cloneMaskShapes(getStoreShapes());
        editDragRef.current = anchor;
        kernel.current?.setIsDrawing(true);
        renderCommittedMask();
        return true;
      }
      const shapeIndex = findShapeContaining(coords);
      if (shapeIndex !== -1) {
        workingShapesRef.current = cloneMaskShapes(getStoreShapes());
        moveShapeRef.current = { shapeIndex };
        moveLastRef.current = coords;
        kernel.current?.setIsDrawing(true);
        renderCommittedMask();
        return true;
      }
      return false;
    }

    // Mask Pen: add a node on an edge (placed where you click — reposition it later with the Select
    // tool), else start a new polygon.
    const edge = findEdgeAt(coords, hitRadius);
    if (edge) {
      const shapes = cloneMaskShapes(getStoreShapes());
      const s = shapes[edge.shapeIndex];
      if (s) {
        s.points.splice(edge.insertIndex, 0, { ...coords });
        persistShapes(shapes, 'Add inpaint mask point');
      }
      return true;
    }
    const startSnap = computeAlignmentSnap(coords, collectAnchorPoints(), Math.max(6, 7 * scale));
    maskDraftPointsRef.current = [{ x: startSnap.x, y: startSnap.y }];
    renderMaskDraft({ x: startSnap.x, y: startSnap.y });
    return true;
  }, [kernelStore, kernel, getFrameRect, findAnchorAt, findEdgeAt, findShapeContaining, collectAnchorPoints, getStoreShapes, persistShapes, renderCommittedMask, commitMaskPolygon, renderMaskDraft]);

  const handleMaskPenMouseMove = useCallback((event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>): boolean => {
    const maskCanvas = maskCanvasRef.current;
    if (!maskCanvas) return false;
    const coords = getCanvasMouseCoordinates(event, maskCanvas, { clamp: true });
    lastHoverPos.current = coords;
    const { activeTool, maskPenMode, isDrawing } = kernelStore.getState();
    const isSelect = activeTool === 'canvasControl' || activeTool === null;
    const working = workingShapesRef.current;

    // Dragging a committed anchor (edit) — Mask Pen or Select tool. Snap to other nodes' axes
    // and show red alignment guides while the node lines up orthogonally with another.
    const drag = editDragRef.current;
    if (drag && working) {
      const s = working[drag.shapeIndex];
      const pt = s?.points[drag.pointIndex];
      if (pt && s) {
        const threshold = Math.max(6, 7 * getWorkspaceRasterDimensions(getFrameRect()).scale);
        const n = s.points.length;
        // Exclude the dragged node and its two path neighbours so adjacent edges never lock to
        // orthogonal; only alignment with other (non-adjacent) nodes snaps.
        const excl = new Set<string>([
          `${drag.shapeIndex}:${drag.pointIndex}`,
          `${drag.shapeIndex}:${(drag.pointIndex - 1 + n) % n}`,
          `${drag.shapeIndex}:${(drag.pointIndex + 1) % n}`,
        ]);
        const snap = computeAlignmentSnap(coords, collectAnchorPoints(excl), threshold);
        pt.x = snap.x;
        pt.y = snap.y;
        renderAlignmentGuides(snap.vX, snap.hY, pt);
      }
      return true;
    }

    // Moving a whole shape (Select tool): translate every point by the cursor delta.
    const move = moveShapeRef.current;
    const last = moveLastRef.current;
    if (move && working && last) {
      const s = working[move.shapeIndex];
      if (s) {
        const dx = coords.x - last.x;
        const dy = coords.y - last.y;
        const { width, height } = getWorkspaceRasterDimensions(getFrameRect());
        const clampedDelta = clampShapeTranslation(s.points, dx, dy, width, height);
        for (const p of s.points) {
          p.x += clampedDelta.dx;
          p.y += clampedDelta.dy;
        }
        renderCommittedMask();
      }
      moveLastRef.current = coords;
      return true;
    }

    if (!isSelect && maskPenMode === 'rect') {
      if (isDrawing) renderMaskDraft(coords);
      return true;
    }
    if (!isSelect && maskPenMode === 'brush') {
      if (isDrawing) {
        brushDraftRef.current = [...brushDraftRef.current, coords];
      }
      renderMaskDraft(coords);
      return true;
    }
    if (!isSelect) renderMaskDraft(coords);
    return true;
  }, [kernelStore, getFrameRect, collectAnchorPoints, renderCommittedMask, renderMaskDraft, renderAlignmentGuides]);

  const handleMaskPenMouseUp = useCallback((): boolean => {
    const { maskPenMode, isDrawing, annotationBrushSize } = kernelStore.getState();
    // Finish an anchor drag / insert or a whole-shape move.
    if (editDragRef.current || moveShapeRef.current) {
      if (workingShapesRef.current) {
        persistShapes(workingShapesRef.current, moveShapeRef.current ? 'Move inpaint mask' : 'Edit inpaint mask point');
      }
      editDragRef.current = null;
      moveShapeRef.current = null;
      moveLastRef.current = null;
      workingShapesRef.current = null;
      kernel.current?.setIsDrawing(false);
      renderAlignmentGuides(null, null); // clear the red guides when the drag ends
      return true;
    }
    // Finish a freehand brush stroke.
    if (maskPenMode === 'brush' && isDrawing) {
      const pts = brushDraftRef.current;
      if (pts.length >= 1) {
        persistShapes(
          [...getStoreShapes(), { kind: 'stroke', points: pts.map((p) => ({ ...p })), width: Math.max(1, annotationBrushSize) }],
          'Draw inpaint mask brush stroke',
        );
      }
      brushDraftRef.current = [];
      kernel.current?.setIsDrawing(false);
      renderMaskDraft(lastHoverPos.current);
    }
    // Finish a rectangle drag: commit the spanning rect as a polygon (ignore tiny accidental drags).
    if (maskPenMode === 'rect' && isDrawing) {
      const start = rectStartRef.current;
      const end = lastHoverPos.current;
      if (start && end) {
        const w = Math.abs(end.x - start.x), h = Math.abs(end.y - start.y);
        if (w >= 3 && h >= 3) {
          persistShapes([...getStoreShapes(), { kind: 'polygon', points: rectCorners(start, end) }], 'Draw inpaint mask rectangle');
        }
      }
      rectStartRef.current = null;
      kernel.current?.setIsDrawing(false);
      renderMaskDraft(lastHoverPos.current);
      return true;
    }
    return true;
  }, [kernelStore, persistShapes, getStoreShapes, kernel, renderMaskDraft, renderAlignmentGuides]);

  useEffect(() => {
    if (isDrawing) return;
    const canvas = canvasRef.current;
    const previewCanvas = previewCanvasRef.current;
    if (!canvas || !previewCanvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { width, height } = getWorkspaceRasterDimensions(frameRect);
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
      previewCanvas.width = width;
      previewCanvas.height = height;
    }

    if (activeWorkspaceAnnotationMask) {
      ctx.putImageData(activeWorkspaceAnnotationMask, 0, 0);
    } else {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }, [activeWorkspaceAnnotationMask, frameRect, isDrawing]);

  // Keep the transparent coordinate canvas sized to the frame raster so pointer mapping stays
  // accurate. The visible mask is the SVG overlay, which re-renders from state automatically.
  useEffect(() => {
    const maskCanvas = maskCanvasRef.current;
    if (!maskCanvas) return;
    const { width, height } = getWorkspaceRasterDimensions(frameRect);
    if (maskCanvas.width !== width || maskCanvas.height !== height) {
      maskCanvas.width = width;
      maskCanvas.height = height;
    }
  }, [frameRect]);

  // Drafting a new polygon is a Mask Pen activity; drop any in-progress draft when leaving it.
  // Re-render committed shapes on every tool change so anchor handles appear/disappear with the
  // active tool (Mask Pen + Select show them; other tools hide them).
  useEffect(() => {
    if (activeTool !== 'maskPen') {
      maskDraftPointsRef.current = [];
      brushDraftRef.current = [];
      rectStartRef.current = null;
      maskOverlayRef.current = EMPTY_MASK_OVERLAY;
    }
    forceMaskRender();
  }, [activeTool, forceMaskRender]);

  // Mask Pen keyboard shortcuts: Enter closes the polygon, Backspace removes the last
  // anchor, Esc cancels the draft (or clears the whole mask when there's no draft).
  useEffect(() => {
    if (activeTool !== 'maskPen' || !isActiveWorkspace) return;
    const onKey = (event: KeyboardEvent) => {
      if (isInertSurface(kernel.current?.canvasContainerRef.current)) return;
      if (event.key === 'Enter') {
        if (maskDraftPointsRef.current.length >= 3) {
          commitMaskPolygon(maskDraftPointsRef.current);
          maskDraftPointsRef.current = [];
          renderMaskDraft(null);
        }
      } else if (event.key === 'Backspace' || event.key === 'Delete') {
        if (maskDraftPointsRef.current.length > 0) {
          event.preventDefault();
          maskDraftPointsRef.current = maskDraftPointsRef.current.slice(0, -1);
          renderMaskDraft(lastHoverPos.current);
        }
      } else if (event.key === 'Escape') {
        if (maskDraftPointsRef.current.length > 0) {
          maskDraftPointsRef.current = [];
          renderMaskDraft(null);
        } else {
          clearInpaintMask();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeTool, isActiveWorkspace, commitMaskPolygon, renderMaskDraft, clearInpaintMask]);

  useEffect(() => {
    if (activeTool === 'annotationLine') return;
    lastPlacedPoint.current = null;
  }, [activeTool]);

  const findCommentNearPoint = useCallback((point: { x: number; y: number }, radius = 20) => {
    const radiusSq = radius * radius;
    return annotationComments.find(c => {
      const dx = c.x - point.x;
      const dy = c.y - point.y;
      return dx * dx + dy * dy <= radiusSq;
    });
  }, [annotationComments]);
  const removeCommentAt = useCallback((point: { x: number; y: number }) => {
    const nearComment = findCommentNearPoint(point);
    if (!nearComment) return;
    kernel.current?.setWorkspace(workspaceId, (workspace) => ({
      ...workspace,
      versions: (workspace.versions ?? []).map((version) => {
        if (version.id !== activeVersionId) return version;
        return {
          ...version,
          annotationComments: (version.annotationComments ?? []).filter((comment) => comment.id !== nearComment.id),
        };
      }),
    }), projectId);
  }, [findCommentNearPoint, kernel, projectId, workspaceId, activeVersionId]);

  const renderBrushPreview = useCallback((coords: { x: number; y: number } | null) => {
    const { activeTool, annotationBrushSize, annotationColor, isDrawing, isShiftKeyPressed } = kernelStore.getState();
    const previewCanvas = previewCanvasRef.current;
    if (!previewCanvas) return;
    const previewCtx = previewCanvas.getContext('2d');
    if (!previewCtx) return;

    previewCtx.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
    if (!isActiveWorkspace) return;
    if (!coords) return;
    if (activeTool !== 'annotationLine' && activeTool !== 'annotationEraser') return;

    const isEraser = activeTool === 'annotationEraser';
    const size = isEraser ? annotationBrushSize * ANNOTATION_ERASER_MULTIPLIER : annotationBrushSize;
    const anchor = lastPlacedPoint.current;

    if (activeTool === 'annotationLine' && !isDrawing && isShiftKeyPressed && anchor) {
      const [r, g, b] = hexToRgb(annotationColor) || [255, 255, 255];
      const lighten = (channel: number) => Math.round(channel + (255 - channel) * 0.5);
      previewCtx.beginPath();
      previewCtx.moveTo(anchor.x, anchor.y);
      previewCtx.lineTo(coords.x, coords.y);
      previewCtx.strokeStyle = `rgba(${lighten(r)}, ${lighten(g)}, ${lighten(b)}, 0.95)`;
      previewCtx.globalAlpha = 1;
      previewCtx.lineCap = 'round';
      previewCtx.lineJoin = 'round';
      previewCtx.lineWidth = Math.max(1, annotationBrushSize * 0.55);
      previewCtx.stroke();
    }

    previewCtx.beginPath();
    previewCtx.arc(coords.x, coords.y, size / 2, 0, 2 * Math.PI);
    previewCtx.strokeStyle = isEraser ? '#ffffff' : annotationColor;
    previewCtx.globalAlpha = 0.9;
    previewCtx.lineWidth = 1.5;
    previewCtx.stroke();
    previewCtx.globalAlpha = 1;
  }, [kernelStore, isActiveWorkspace]);

  useEffect(() => {
    lastPlacedPoint.current = null;
    strokeStartPos.current = null;
    strokeMoved.current = false;
    lastHoverPos.current = null;
    const previewCanvas = previewCanvasRef.current;
    const previewCtx = previewCanvas?.getContext('2d');
    if (previewCanvas && previewCtx) {
      previewCtx.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
    }
  }, [workspaceId]);

  useEffect(() => {
    if (isActiveWorkspace) return;
    lastHoverPos.current = null;
    const previewCanvas = previewCanvasRef.current;
    const previewCtx = previewCanvas?.getContext('2d');
    if (previewCanvas && previewCtx) {
      previewCtx.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
    }
  }, [isActiveWorkspace]);

  // renderBrushPreview is stable and reads the tool state at call time, so this effect keeps the
  // subscribed values as its trigger: the preview must redraw under a motionless cursor whenever the
  // size / colour / tool / shift-anchor state changes.
  useEffect(() => {
    renderBrushPreview(lastHoverPos.current);
  }, [activeTool, annotationBrushSize, annotationColor, isDrawing, isShiftKeyPressed, isActiveWorkspace]);

  const handleMouseDown = useCallback(async (event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    const isTouch = 'touches' in event;
    if (!isTouch && (event as React.MouseEvent).button !== 0) return false;

    const { activeTool } = kernelStore.getState();
    if (activeTool === 'maskPen' || activeTool === 'canvasControl') {
      return handleMaskPenMouseDown(event);
    }

    const canvas = canvasRef.current;
    if (!canvas) return false;

    const coords = getCanvasMouseCoordinates(event, canvas, { clamp: true });

    if (activeTool === 'comment' || activeTool === null) {
      const existing = findCommentNearPoint(coords);
      if (existing) {
        // Open the comment's thread panel (comment + replies + reply composer).
        setPendingComment(null);
        setOpenThreadId(existing.id);
        return true;
      }

      if (activeTool === 'comment') {
        setOpenThreadId(null);
        setPendingComment({ id: uuidv4(), x: coords.x, y: coords.y, mode: 'new', color: COMMENT_COLOR });
        setPendingCommentText('');
        return true;
      }
    }

    // Select tool: grab an existing mask shape/anchor to move or edit it. A miss returns false so
    // the click falls through to the normal select behaviour (layer selection / pan).
    if (activeTool === null) {
      if (handleMaskPenMouseDown(event)) return true;
    }

    if (activeTool !== 'annotationLine' && activeTool !== 'annotationEraser') return false;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return false;

    kernel.current?.setIsDrawing(true);
    strokeStartPos.current = coords;
    strokeMoved.current = false;
    lastMousePos.current = coords;

    if (activeTool === 'annotationEraser') {
      applyEraserStroke(ctx, coords, coords);
      removeCommentAt(coords);
    } else {
      stampBrush(ctx, coords, 'draw');
    }

    return true;
  }, [kernelStore, stampBrush, removeCommentAt, applyEraserStroke, findCommentNearPoint, kernel, handleMaskPenMouseDown]);

  const handleMouseMove = useCallback((event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    const { activeTool, annotationBrushSize, isDrawing } = kernelStore.getState();
    if (activeTool === 'maskPen' || activeTool === 'canvasControl') {
      return handleMaskPenMouseMove(event);
    }

    // Select tool: keep dragging a grabbed mask shape/anchor.
    if (activeTool === null && (editDragRef.current || moveShapeRef.current)) {
      return handleMaskPenMouseMove(event);
    }

    const canvas = canvasRef.current;
    const previewCanvas = previewCanvasRef.current;
    if (!canvas || !previewCanvas) return false;

    const coords = getCanvasMouseCoordinates(event, canvas, { clamp: true });
    lastHoverPos.current = coords;
    renderBrushPreview(coords);

    if (!isDrawing) return activeTool === 'annotationLine' || activeTool === 'annotationEraser';

    const ctx = canvas.getContext('2d');
    if (!ctx) return false;

    if (activeTool === 'annotationLine' && !strokeMoved.current && strokeStartPos.current) {
      const deltaX = coords.x - strokeStartPos.current.x;
      const deltaY = coords.y - strokeStartPos.current.y;
      if (Math.hypot(deltaX, deltaY) > POLYGON_CLICK_MOVE_TOLERANCE_PX) {
        strokeMoved.current = true;
      }
    }

    const prevPoint = lastMousePos.current ?? coords;
    const dx = coords.x - prevPoint.x;
    const dy = coords.y - prevPoint.y;
    const distance = Math.hypot(dx, dy);
    const step = Math.max(1, annotationBrushSize / 3);
    const steps = Math.max(1, Math.ceil(distance / step));

    if (activeTool === 'annotationEraser') {
      applyEraserStroke(ctx, prevPoint, coords);
      for (let i = 0; i <= steps; i++) {
        const t = steps === 0 ? 0 : i / steps;
        const point = { x: prevPoint.x + dx * t, y: prevPoint.y + dy * t };
        removeCommentAt(point);
      }
    } else {
      for (let i = 0; i <= steps; i++) {
        const t = steps === 0 ? 0 : i / steps;
        const point = { x: prevPoint.x + dx * t, y: prevPoint.y + dy * t };
        stampBrush(ctx, point, 'draw');
      }
    }
    lastMousePos.current = coords;
    return true;
  }, [kernelStore, stampBrush, applyEraserStroke, removeCommentAt, renderBrushPreview, handleMaskPenMouseMove]);

  const handleMouseUp = useCallback((event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    const { activeTool, isDrawing, isShiftKeyPressed } = kernelStore.getState();
    if (activeTool === 'maskPen' || activeTool === 'canvasControl') {
      return handleMaskPenMouseUp();
    }
    // Select tool: finish a grabbed-mask drag (persist + clear refs).
    if (activeTool === null && (editDragRef.current || moveShapeRef.current)) {
      return handleMaskPenMouseUp();
    }
    if (!isDrawing) return false;

    const canvas = canvasRef.current;
    if (!canvas) return false;
    const ctx = canvas.getContext('2d');
    if (!ctx) return false;
    const coords = getCanvasMouseCoordinates(event, canvas, { clamp: true });
    const isShiftPressed = isShiftKeyPressed;

    lastMousePos.current = null;

    ctx.closePath();

    if (activeTool === 'annotationLine') {
      if (!strokeMoved.current && isShiftPressed && lastPlacedPoint.current) {
        drawBrushedSegment(ctx, lastPlacedPoint.current, coords);
      }
      // Always keep the latest stroke endpoint as anchor so shift-click can chain
      // from the end of either a point stroke or a freehand line stroke.
      lastPlacedPoint.current = coords;
    }

    void persistWorkspaceAnnotationMask(ctx, canvas)
      .finally(() => {
        kernel.current?.setIsDrawing(false);
      });
    strokeStartPos.current = null;
    strokeMoved.current = false;
    return true;
  }, [
    kernelStore,
    kernel,
    drawBrushedSegment,
    persistWorkspaceAnnotationMask,
    handleMaskPenMouseUp,
  ]);

  const saveComment = useCallback(async () => {
    if (!pendingComment) return;
    const text = pendingCommentText.trim();
    const commentColor = pendingComment.color ?? COMMENT_COLOR;

    if (text) {
      const kernelHandle = kernel.current;
      if (!kernelHandle) {
        setPendingComment(null);
        return;
      }
      const targetVersionId = await kernelHandle.ensureWritableAnnotationVersion(workspaceId);
      if (!targetVersionId) {
        setPendingComment(null);
        return;
      }

      const userContext = kernel.current?.getUserContext();
      const authorName = userContext?.isImpersonating
        ? (userContext.impersonatedUser?.name || 'Support Agent')
        : (userContext?.trueUser?.name || 'User');

      kernel.current?.setWorkspace(workspaceId, (workspace) => {
        const targetVersion = (workspace.versions ?? []).find((version) => version.id === targetVersionId) ?? null;
        const existing = targetVersion?.annotationComments || [];
        const updated = pendingComment.mode === 'edit'
          ? existing.map(c => c.id === pendingComment.id ? { ...c, text } : c)
          : [...existing, {
              id: pendingComment.id,
              x: pendingComment.x,
              y: pendingComment.y,
              text,
              color: commentColor,
              author: authorName,
              authorAvatarUrl: currentUserAvatarUrl,
              timestamp: Date.now()
            }];
        return {
          ...workspace,
          versions: (workspace.versions ?? []).map((version) => {
            if (version.id !== targetVersionId) return version;
            return {
              ...version,
              annotationComments: updated,
            };
          }),
        };
      }, projectId);
    }
    setPendingComment(null);
  }, [pendingComment, pendingCommentText, kernel, workspaceId, projectId, currentUserAvatarUrl]);

  const deleteComment = useCallback(async () => {
    if (!pendingComment) return;
    const kernelHandle = kernel.current;
    if (!kernelHandle) {
      setPendingComment(null);
      return;
    }
    const targetVersionId = await kernelHandle.ensureWritableAnnotationVersion(workspaceId);
    if (!targetVersionId) {
      setPendingComment(null);
      return;
    }

    kernel.current?.setWorkspace(workspaceId, (workspace) => ({
      ...workspace,
      versions: (workspace.versions ?? []).map((version) => {
        if (version.id !== targetVersionId) return version;
        return {
          ...version,
          annotationComments: (version.annotationComments ?? []).filter((comment) => comment.id !== pendingComment.id),
        };
      }),
    }), projectId);
    setPendingComment(null);
  }, [pendingComment, kernel, workspaceId, projectId]);

  // Apply a mutation to the active version's comment list (write-through to the workspace).
  const mutateAnnotationComments = useCallback(async (
    mutate: (comments: AnnotationComment[]) => AnnotationComment[]
  ) => {
    const kernelHandle = kernel.current;
    if (!kernelHandle) return;
    const targetVersionId = await kernelHandle.ensureWritableAnnotationVersion(workspaceId);
    if (!targetVersionId) return;
    kernelHandle.setWorkspace(workspaceId, (workspace) => ({
      ...workspace,
      versions: (workspace.versions ?? []).map((version) =>
        version.id !== targetVersionId
          ? version
          : { ...version, annotationComments: mutate(version.annotationComments ?? []) }
      ),
    }), projectId);
  }, [kernel, workspaceId, projectId]);

  const addReply = useCallback(async (commentId: string) => {
    const text = replyText.trim();
    if (!text || !kernel.current) return;
    const userContext = kernel.current.getUserContext();
    const authorName = userContext?.isImpersonating
      ? (userContext.impersonatedUser?.name || 'Support Agent')
      : (userContext?.trueUser?.name || 'User');
    const reply = { id: uuidv4(), text, author: authorName, authorAvatarUrl: currentUserAvatarUrl, timestamp: Date.now() };
    await mutateAnnotationComments((comments) =>
      comments.map((c) => (c.id === commentId ? { ...c, replies: [...(c.replies ?? []), reply] } : c))
    );
    setReplyText('');
  }, [replyText, kernel, currentUserAvatarUrl, mutateAnnotationComments]);

  const resolveThread = useCallback(async (commentId: string) => {
    await mutateAnnotationComments((comments) => comments.filter((c) => c.id !== commentId));
    setOpenThreadId(null);
  }, [mutateAnnotationComments]);

  const deleteReply = useCallback(async (commentId: string, replyId: string) => {
    await mutateAnnotationComments((comments) =>
      comments.map((c) => (c.id === commentId ? { ...c, replies: (c.replies ?? []).filter((r) => r.id !== replyId) } : c))
    );
  }, [mutateAnnotationComments]);

  const selectCommentThread = useCallback((commentId: string) => {
    setPendingComment(null);
    setOpenThreadId(commentId);
  }, []);
  const cancelPendingComment = useCallback(() => setPendingComment(null), []);
  const closeThread = useCallback(() => setOpenThreadId(null), []);
  const resolveOpenThread = useCallback(() => {
    if (openThreadId) void resolveThread(openThreadId);
  }, [openThreadId, resolveThread]);
  const submitOpenThreadReply = useCallback(() => {
    if (openThreadId) void addReply(openThreadId);
  }, [openThreadId, addReply]);
  const deleteOpenThreadRow = useCallback((rowId: string, isRoot: boolean) => {
    if (!openThreadId) return;
    if (isRoot) void resolveThread(openThreadId);
    else void deleteReply(openThreadId, rowId);
  }, [openThreadId, resolveThread, deleteReply]);
  const pendingCommentAvatar = useMemo(() => ({
    imageUrl: currentUserAvatarUrl,
    alt: 'You',
    color: pendingComment?.color ?? COMMENT_COLOR,
  }), [currentUserAvatarUrl, pendingComment?.color]);

  const hasActiveMaskDraft = useCallback(() => (
    maskDraftPointsRef.current.length > 0 ||
    brushDraftRef.current.length > 0 ||
    rectStartRef.current !== null ||
    editDragRef.current !== null ||
    moveShapeRef.current !== null
  ), []);

  useImperativeHandle(ref, () => ({
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    hasActiveMaskDraft,
  }), [handleMouseDown, handleMouseMove, handleMouseUp, hasActiveMaskDraft]);

  const { scale } = getWorkspaceRasterDimensions(frameRect);
  const displayScale = 1.0 / scale;

  return (
    <>
      <canvas
        ref={canvasRef}
        className="absolute top-0 left-0 pointer-events-none"
        style={annotationCanvasStyle}
      />
      <canvas
        ref={previewCanvasRef}
        className="absolute top-0 left-0 pointer-events-none"
        style={PREVIEW_CANVAS_STYLE}
      />
      {/* Inpaint mask: transparent canvas kept only as the pointer-coordinate reference. */}
      <canvas
        ref={maskCanvasRef}
        className="absolute top-0 left-0 pointer-events-none"
        style={MASK_CANVAS_STYLE}
      />
      {/* Inpaint mask vector overlay: true vector, crisp at any zoom. */}
      {(() => {
        if (masksHidden) return null;
        const { width: vbW, height: vbH } = getWorkspaceRasterDimensions(frameRect);
        if (vbW <= 0 || vbH <= 0) return null;
        const liveShapes = workingShapesRef.current ?? inpaintShapes ?? [];
        const ov = maskOverlayRef.current;
        const maskShowAnchors = isActiveWorkspace && (activeTool === 'maskPen' || activeTool === 'canvasControl' || activeTool === null);
        const ptsStr = (ps: MaskPoint[]) => ps.map((p) => `${p.x},${p.y}`).join(' ');
        const outlineFilterId = `${maskSvgIdBase}-outline`;
        const renderMaskUnionShape = (s: MaskShape, i: number, variant: 'outline' | 'fill') => {
          const color = variant === 'outline' ? '#ffffff' : MASK_FILL;
          if (s.kind === 'polygon') {
            if (s.points.length < 3) return null;
            return (
              <polygon
                key={`${variant}-s${i}`}
                points={ptsStr(s.points)}
                fill={color}
                stroke="none"
              />
            );
          }
          const sp0 = s.points[0];
          if (s.points.length === 1) {
            return sp0
              ? (
                <circle
                  key={`${variant}-s${i}`}
                  cx={sp0.x}
                  cy={sp0.y}
                  r={Math.max(1, (s.width ?? 2) / 2)}
                  fill={variant === 'outline' ? '#ffffff' : MASK_STROKE_FILL}
                />
              )
              : null;
          }
          return (
            <polyline
              key={`${variant}-s${i}`}
              points={ptsStr(s.points)}
              fill="none"
              stroke={variant === 'outline' ? '#ffffff' : MASK_STROKE_FILL}
              strokeWidth={Math.max(1, s.width ?? 2)}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          );
        };
        const anchorDot = (p: MaskPoint, key: string) => (
          <g key={key}>
            <line x1={p.x} y1={p.y} x2={p.x} y2={p.y} stroke={MASK_OUTLINE} strokeWidth={4} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
            <line x1={p.x} y1={p.y} x2={p.x} y2={p.y} stroke={MASK_ANCHOR_FILL} strokeWidth={2.5} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
          </g>
        );
        return (
          <svg
            className="absolute top-0 left-0 pointer-events-none"
            style={MASK_SVG_STYLE}
            viewBox={`0 0 ${vbW} ${vbH}`}
            preserveAspectRatio="none"
          >
            <defs>
              <filter id={outlineFilterId} x="-8%" y="-8%" width="116%" height="116%" colorInterpolationFilters="sRGB">
                <feMorphology in="SourceAlpha" operator="dilate" radius="1.5" result="dilated" />
                <feComposite in="dilated" in2="SourceAlpha" operator="out" result="outline" />
                <feFlood floodColor={MASK_OUTLINE} floodOpacity="1" result="outlineColor" />
                <feComposite in="outlineColor" in2="outline" operator="in" />
              </filter>
            </defs>
            <g filter={`url(#${outlineFilterId})`}>
              {liveShapes.map((s, i) => renderMaskUnionShape(s, i, 'outline'))}
            </g>
            <g>
              {liveShapes.map((s, i) => renderMaskUnionShape(s, i, 'fill'))}
            </g>
            {maskShowAnchors && liveShapes.map((s, i) => (s.kind === 'polygon' ? s.points.map((p, j) => anchorDot(p, `a${i}-${j}`)) : null))}
            {ov.brush && ov.brush.length === 1 && ov.brush[0] && (
              <circle cx={ov.brush[0].x} cy={ov.brush[0].y} r={Math.max(0.5, annotationBrushSize / 2)} fill={MASK_STROKE_FILL} />
            )}
            {ov.brush && ov.brush.length > 1 && (
              <polyline points={ptsStr(ov.brush)} fill="none" stroke={MASK_STROKE_FILL} strokeWidth={Math.max(1, annotationBrushSize)} strokeLinecap="round" strokeLinejoin="round" />
            )}
            {ov.brushCursor && (
              <circle cx={ov.brushCursor.x} cy={ov.brushCursor.y} r={Math.max(0.5, annotationBrushSize / 2)} fill="none" stroke={MASK_OUTLINE} strokeWidth={1.25} vectorEffect="non-scaling-stroke" />
            )}
            {ov.draft.length > 0 && (
              <polyline
                points={ptsStr(ov.rubber ? [...ov.draft, ov.rubber] : ov.draft)}
                fill="none"
                stroke={MASK_OUTLINE}
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            )}
            {ov.draft.map((p, j) => anchorDot(p, `d${j}`))}
            {ov.guideVX !== null && (
              <line x1={ov.guideVX} y1={0} x2={ov.guideVX} y2={vbH} stroke={MASK_GUIDE} strokeWidth={0.5} vectorEffect="non-scaling-stroke" />
            )}
            {ov.guideHY !== null && (
              <line x1={0} y1={ov.guideHY} x2={vbW} y2={ov.guideHY} stroke={MASK_GUIDE} strokeWidth={0.5} vectorEffect="non-scaling-stroke" />
            )}
            {ov.snap && (
              <g stroke={MASK_GUIDE} strokeWidth={0.75} strokeLinecap="round" vectorEffect="non-scaling-stroke">
                <line x1={ov.snap.x - 4} y1={ov.snap.y - 4} x2={ov.snap.x + 4} y2={ov.snap.y + 4} />
                <line x1={ov.snap.x - 4} y1={ov.snap.y + 4} x2={ov.snap.x + 4} y2={ov.snap.y - 4} />
              </g>
            )}
          </svg>
        );
      })()}

      {/* Comment markers: avatar pin + hover card */}
      <div className="absolute inset-0 pointer-events-none">
        {annotationComments.map((comment, index) => (
          <CommentPin
            key={comment.id}
            commentId={comment.id}
            index={index}
            x={comment.x}
            y={comment.y}
            displayScale={displayScale}
            text={comment.text}
            color={comment.color}
            author={comment.author}
            authorAvatarUrl={comment.authorAvatarUrl}
            timestamp={comment.timestamp}
            onSelect={selectCommentThread}
          />
        ))}
      </div>

      {/* Floating Comment Input */}
      {pendingComment && (
        <FloatingCanvasInput
          value={pendingCommentText}
          placeholder="Add a comment"
          submitTitle="Comment"
          avatar={pendingCommentAvatar}
          left={`calc(${pendingComment.x * displayScale}px + (10px / ${zoomLevelCssVar}))`}
          top={`calc(${pendingComment.y * displayScale}px + (10px / ${zoomLevelCssVar}))`}
          transform={`scale(${inverseZoomLevelCssVar})`}
          onChange={setPendingCommentText}
          onSubmit={saveComment}
          onCancel={cancelPendingComment}
          onDelete={pendingComment.mode === 'edit' ? deleteComment : undefined}
          deleteTitle="Delete comment"
        />
      )}

      {/* Comment thread panel (comment + replies + reply composer) */}
      {(() => {
        if (!openThreadId) return null;
        const thread = annotationComments.find((c) => c.id === openThreadId);
        if (!thread) return null;
        return (
          <CommentThreadPanel
            threadId={thread.id}
            x={thread.x}
            y={thread.y}
            displayScale={displayScale}
            text={thread.text}
            author={thread.author}
            authorAvatarUrl={thread.authorAvatarUrl}
            timestamp={thread.timestamp}
            replies={thread.replies}
            replyText={replyText}
            currentUserAvatarUrl={currentUserAvatarUrl}
            onResolve={resolveOpenThread}
            onClose={closeThread}
            onReplyTextChange={setReplyText}
            onReplySubmit={submitOpenThreadReply}
            onDeleteRow={deleteOpenThreadRow}
          />
        );
      })()}
    </>
  );
}));

AnnotationCanvas.displayName = 'AnnotationCanvas';
export default AnnotationCanvas;
